import type { ICoordinate2D, IPolygon } from "../schemas";
import type { IMcBimItem, IObb3DWorldMm, IVec3 } from "../schemas/mcbim.schema";
import { getRoomAxes } from "./getRoomAxes";
import { projectObbsForDirection } from "./projectObbsForDirection";

export type IRoomSectionDirection = "sideA" | "sideB" | "sideC" | "sideD" | "isometric";

export type IRoomSectionPolygon = {
  polygon: IPolygon;
  /** 0–1 shade factor; 1 = fully lit, ~0.25 = darkest shadow */
  shade: number;
};

export type IRoomSectionItem = {
  mcBimItem: IMcBimItem;
  sectionPolygons: IRoomSectionPolygon[];
};

export type IRoomSectionView = {
  direction: IRoomSectionDirection;
  sections: IRoomSectionItem[];
  /** Projected floor polygon, or null if room polygon unavailable */
  floorPolygon: IPolygon | null;
};

export type IRoomSectionViews = Record<IRoomSectionDirection, IRoomSectionView>;
export type IRoomSectionDirectionCoordinates = Record<IRoomSectionDirection, ICoordinate2D>;
export type IRoomSectionDirectionPolygons = Record<IRoomSectionDirection, IPolygon>;

export type BuildRoomSectionViewsFromMcBimItemsArgs = {
  roomItems: IMcBimItem[];
  mcbimSpace: IMcBimItem;
  sectionCoordinate: ICoordinate2D;
};



/**
 * Returns the 2D view direction for each side, derived from the same arrow logic so cameras
 * and arrows are always consistent. The arrow tip points AWAY from the camera (toward the wall),
 * so the camera viewDir is the negation of the arrow tip direction.
 */
export const getRoomSectionViewDirs = (mcbimSpace: IMcBimItem): Record<"sideA" | "sideB" | "sideC" | "sideD", [number, number]> => {
  const { axisA } = getRoomAxes(mcbimSpace);
  // Arrow tip directions (derived from arrow polygon geometry):
  //   sideD tip: axisB = (-axisA.y, axisA.x)
  //   sideC tip: axisA = (axisA.x, axisA.y)
  //   sideB tip: -axisB = (axisA.y, -axisA.x)
  //   sideA tip: -axisA = (-axisA.x, -axisA.y)
  // Camera viewDir = negate of arrow tip (camera looks from arrow toward wall):
  return {
    sideA: [axisA.x, axisA.y],
    sideB: [-axisA.y, axisA.x],
    sideC: [-axisA.x, -axisA.y],
    sideD: [axisA.y, -axisA.x],
  };
};

export const buildRoomSectionDirectionPolygons = (
  centerCoordinate: ICoordinate2D,
  mcbimSpace: IMcBimItem,
): IRoomSectionDirectionPolygons => {
  const base = 1.0;
  const dist = 0.1;
  const baseTriangle: IPolygon = [[0, base + dist], [-base/2, base/2 + dist], [base/2, base/2 + dist], [0, base + dist]];

  const { axisA } = getRoomAxes(mcbimSpace);
  const sideD = alignPolygonToAxis2D(baseTriangle, [axisA.x, axisA.y]);
  const sideC = rotatePolygon90DegCW(sideD);
  const sideB = rotatePolygon90DegCW(sideC);
  const sideA = rotatePolygon90DegCW(sideB);

  // axisB is 90° CCW from axisA: (-axisA.y, axisA.x)
  // Iso camera looks along (axisA + axisB) in 2D, i.e. diagonally into the room.
  // Camera sits at the -(axisA + axisB) corner, so cornerDirection = -(axisA + axisB).
  const isoViewX = axisA.x + (-axisA.y);  // axisA.x + axisB.x
  const isoViewY = axisA.y + axisA.x;     // axisA.y + axisB.y
  const isoLen = Math.sqrt(isoViewX * isoViewX + isoViewY * isoViewY);
  const isoDir2D: [number, number] = [isoViewX / isoLen, isoViewY / isoLen];
  const cornerDirection: [number, number] = [-isoDir2D[0], -isoDir2D[1]];

  const roomPolygon = mcbimSpace.geometry?.polygon2d ?? mcbimSpace.geometry?.bbox2d ?? [];
  const roomCorner = roomPolygon.reduce<{ point: [number, number]; proj: number }>((best, point) => {
    const proj = point[0] * cornerDirection[0] + point[1] * cornerDirection[1];
    return proj > best.proj ? { point: point as [number, number], proj } : best;
  }, { point: centerCoordinate as [number, number], proj: -Infinity }).point;

  // Arrow points along isoDir2D: alignPolygonToAxis2D(base, [dy, -dx]) makes tip point along (dx, dy)
  const isometric = alignPolygonToAxis2D(baseTriangle, [isoDir2D[1], -isoDir2D[0]]);

  const translate = (poly: IPolygon, offset: ICoordinate2D): IPolygon =>
    poly.map(([x, y]) => [x + offset[0], y + offset[1]]);

  return {
    sideA: translate(sideA, centerCoordinate),
    sideB: translate(sideB, centerCoordinate),
    sideC: translate(sideC, centerCoordinate),
    sideD: translate(sideD, centerCoordinate),
    isometric:translate(isometric, [roomCorner[0] + cornerDirection[0] * 0.25, roomCorner[1] + cornerDirection[1] * 0.25]),
  };
};

/** Projects polygon points from local XY into the orientation defined by axisA (2D unit vector). */
export const alignPolygonToAxis2D = (polygon: IPolygon, axisA: [number, number]): IPolygon => {
  const [ax, ay] = axisA;
  return polygon.map(([x, y]) => [x * ax - y * ay, x * ay + y * ax]);
};

/** Rotates a polygon 90° clockwise around the origin: (x, y) → (y, −x) */
export const rotatePolygon90DegCW = (polygon: IPolygon): IPolygon =>
  polygon.map(([x, y]) => [y, -x]);

/** Rotates a polygon 45° counter-clockwise around the origin */
const _cos45 = Math.cos(Math.PI / 4);
const _sin45 = Math.sin(Math.PI / 4);
export const rotatePolygon45DegCCW = (polygon: IPolygon): IPolygon =>
  polygon.map(([x, y]) => [x * _cos45 - y * _sin45, x * _sin45 + y * _cos45]);

const normalize = (v: IVec3): IVec3 => {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  return len < 1e-9 ? { x: 1, y: 0, z: 0 } : { x: v.x / len, y: v.y / len, z: v.z / len };
};

const cross = (a: IVec3, b: IVec3): IVec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});

/**
 * Extracts all OBBs from an McBimItem, if present.
 */
const getObbs = (item: IMcBimItem): IObb3DWorldMm[] =>
  item.geometry?.Obb3DWorldMmList ?? [];

/**
 * Builds projected section polygons for a single item viewed from a given direction.
 * Returns one polygon per visible OBB face (painter's order, shaded).
 * Falls back to an empty array if the item has no OBBs.
 */
const buildSectionPolygons = (
  item: IMcBimItem,
  viewDir: IVec3,
  right: IVec3,
  lightDir?: IVec3
): IRoomSectionPolygon[] => {
  const obbs = getObbs(item);
  if (obbs.length === 0) return [];

  return projectObbsForDirection(obbs, viewDir, right, lightDir).map((f) => ({
    polygon: f.polygon,
    shade: f.shade,
  }));
};

/**
 * Builds section items for all room items from a given view direction.
 * Items with no OBBs are omitted.
 */
const buildSectionItems = (
  roomItems: IMcBimItem[],
  viewDir: IVec3,
  right: IVec3,
  lightDir?: IVec3
): IRoomSectionItem[] => {
  const result: IRoomSectionItem[] = [];

  for (const item of roomItems) {
    const polygons = buildSectionPolygons(item, viewDir, right, lightDir);
    if (polygons.length > 0) {
      result.push({ mcBimItem: item, sectionPolygons: polygons });
    }
  }

  return result;
};

export const buildRoomSectionViewsFromMcBimItems = (
  args: BuildRoomSectionViewsFromMcBimItemsArgs
): IRoomSectionViews => {
  const { roomItems, mcbimSpace } = args;

  // Derive axis-aligned viewDirs from the same source as the arrows for guaranteed consistency
  const viewDirs2D = getRoomSectionViewDirs(mcbimSpace);
  const toVec3 = ([x, y]: [number, number]): IVec3 => ({ x, y, z: 0 });
  const toRight = ([x, y]: [number, number]): IVec3 => ({ x: y, y: -x, z: 0 }); // 90° CW = right vector

  // Isometric: view along (axisA + axisB) diagonally, same direction as the arrow
  const { axisA: isoAxisA } = getRoomAxes(mcbimSpace);
  const isoViewDir = normalize({ x: isoAxisA.x + (-isoAxisA.y), y: isoAxisA.y + isoAxisA.x, z: -1 });
  const isoRight   = normalize(cross({ x: 0, y: 0, z: 1 }, isoViewDir));
  const isoLight: IVec3 = { x: 0, y: 1, z: 0 };

  const sides: { direction: IRoomSectionDirection; viewDir: IVec3; right: IVec3; lightDir?: IVec3 }[] = [
    { direction: "sideA",     viewDir: toVec3(viewDirs2D.sideA), right: toRight(viewDirs2D.sideA) },
    { direction: "sideB",     viewDir: toVec3(viewDirs2D.sideB), right: toRight(viewDirs2D.sideB) },
    { direction: "sideC",     viewDir: toVec3(viewDirs2D.sideC), right: toRight(viewDirs2D.sideC) },
    { direction: "sideD",     viewDir: toVec3(viewDirs2D.sideD), right: toRight(viewDirs2D.sideD) },
    { direction: "isometric", viewDir: isoViewDir, right: isoRight, lightDir: isoLight },
  ];

  const result = {} as IRoomSectionViews;

  for (const { direction, viewDir, right, lightDir } of sides) {
    result[direction] = {
      direction,
      floorPolygon: null,
      sections: buildSectionItems(roomItems, viewDir, right, lightDir),
    };
  }

  return result;
};
